'use client';

import { useCallback, useEffect, useRef } from 'react';
import { trpc, trpcVanilla } from '@/lib/trpc';
import { useRegistrationStore } from '../../stores/registration-store';
import { trackRego, describeSaveError } from '../../lib/registration-analytics';

const SAVE_DEBOUNCE_MS = 500;
/** Hard cap on the pre-redirect flush so a slow save can't block checkout. */
const FLUSH_TIMEOUT_MS = 2_000;

/**
 * Debounced server persistence of the steps-3/4 form buffer to
 * entrant_history.checkout_state. Extracted from the DGR register page.
 *
 * `flush()` saves immediately (bounded by FLUSH_TIMEOUT_MS) — call it before
 * redirecting to hosted checkout so a cancel-return restores the exact cart.
 */
export function useCheckoutStatePersistence(input: {
  enabled: boolean;
  eventId: string | null;
  currentStep: number;
}) {
  const { enabled, eventId, currentStep } = input;
  const saveCheckoutStateMutation = trpc.publicEntrant.saveCheckoutState.useMutation();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getCheckoutStateSnapshot = useRegistrationStore((s) => s.getCheckoutStateSnapshot);
  // Subscribe to every persisted field so the debounce effect re-runs on change
  const newsletterOptIn = useRegistrationStore((s) => s.newsletterOptIn);
  const marketingOptIns = useRegistrationStore((s) => s.marketingOptIns);
  const donationAmount = useRegistrationStore((s) => s.donationAmount);
  const customDonation = useRegistrationStore((s) => s.customDonation);
  const selectedMerch = useRegistrationStore((s) => s.selectedMerch);
  const shippingAddress = useRegistrationStore((s) => s.shippingAddress);
  const shippingAddressLine2 = useRegistrationStore((s) => s.shippingAddressLine2);
  const shippingCity = useRegistrationStore((s) => s.shippingCity);
  const shippingState = useRegistrationStore((s) => s.shippingState);
  const shippingPostcode = useRegistrationStore((s) => s.shippingPostcode);
  const shippingCountry = useRegistrationStore((s) => s.shippingCountry);

  const buildCheckoutState = useCallback(
    () => ({
      ...getCheckoutStateSnapshot(),
      lastReachedStep: currentStep === 4 ? 4 : 3,
    }),
    [getCheckoutStateSnapshot, currentStep],
  );

  useEffect(() => {
    if (!enabled || currentStep < 3) return;

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      saveCheckoutStateMutation.mutate(
        { eventId: eventId || undefined, checkoutState: buildCheckoutState() },
        {
          onError: (err) => {
            trackRego('rego_error', {
              step: 'checkout',
              error_type: 'save_state_failed',
              ...describeSaveError(err),
            });
          },
        },
      );
    }, SAVE_DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    currentStep,
    eventId,
    newsletterOptIn,
    marketingOptIns,
    donationAmount,
    customDonation,
    selectedMerch,
    shippingAddress,
    shippingAddressLine2,
    shippingCity,
    shippingState,
    shippingPostcode,
    shippingCountry,
  ]);

  const flush = useCallback(async () => {
    if (!enabled) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    try {
      await Promise.race([
        trpcVanilla.publicEntrant.saveCheckoutState.mutate({
          eventId: eventId || undefined,
          checkoutState: buildCheckoutState(),
        }),
        new Promise((resolve) => setTimeout(resolve, FLUSH_TIMEOUT_MS)),
      ]);
    } catch (err) {
      // Best-effort: the redirect must not be blocked by a failed state save —
      // but a failed flush silently breaks cancel-return cart restore, so record
      // it (previously this catch was empty and flush failures were invisible).
      trackRego('rego_error', {
        step: 'checkout',
        error_type: 'flush_state_failed',
        ...describeSaveError(err),
      });
    }
  }, [enabled, eventId, buildCheckoutState]);

  return { flush };
}
